import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { FaroSimpleTextField } from "@components/common/faro-text-field/faro-simple-text-field";
import { EditColumnValueIcon } from "@components/table/edit-column-value-icon";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseSnapshotProps } from "@custom-types/base-types";
import { neutral, NO_TRANSLATE_CLASS, Text, useToast } from "@flat-ui";
import { Box, Stack } from "@mui/material";
import { useAppDispatch } from "@store/store-helper";
import { SnapshotEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getBottomBorderWithBoxShadow } from "@utils/ui-utils";
import { useEffect, useState } from "react";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { withEllipsis } from "@styles/common-styles";
import { updateSnapshotDetails } from "@store/projects/projects-slice-thunk";

/** Class name of snapshot name edit icon, Used to control the visibility on hover */
const SNAPSHOT_NAME_EDIT_ICON_CLASS = "snapshot-name-edit";

/** Component that renders the snapshot name with ability to edit */
export function SnapshotName({ snapshot }: BaseSnapshotProps): JSX.Element {
  const { handleErrorWithToast } = useErrorContext();
  const { openToast } = useToast();
  const dispatch = useAppDispatch();

  const { trackEvent } = useTrackEvent();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [snapshotName, setSnapshotName] = useState<string>(snapshot.name);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * This hook sets the state variable snapshotName to the value of snapshot.name whenever the snapshot object changes.
   * This ensures that the snapshotName state is kept in sync with the snapshot object, allowing updates to
   * be reflected in multiple instances of the component
   */
  useEffect(() => {
    setSnapshotName(snapshot.name);
  }, [snapshot]);

  /** Triggers when user clicks on check mark after update */
  async function updateName(): Promise<void> {
    setIsEditMode(false);

    trackEvent({
      name: SnapshotEvents.editSnapshot,
      props: {
        isNameChanged: snapshotName !== snapshot.name,
        nameLength: snapshotName.length,
      },
    });

    const trimmedSnapshotName = snapshotName.trim();

    // Early return after reset the name if snapshot name has empty value
    if (!trimmedSnapshotName.length) {
      setSnapshotName(snapshot.name);
      return;
    }

    // Early return if the snapshot name is not changed
    if (trimmedSnapshotName === snapshot.name) {
      return;
    }

    setIsLoading(true);

    try {
      await dispatch(
        updateSnapshotDetails({
          snapshotId: snapshot.id,
          payload: { name: trimmedSnapshotName },
        })
      ).unwrap();

      openToast({
        title: "Snapshot name changed successfully.",
      });
    } catch (error) {
      setSnapshotName(snapshot.name);

      handleErrorWithToast({
        id: `updateSnapshotName-${Date.now().toString()}`,
        title: "Could not change snapshot name. Please try again",
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  let EndIcon: React.ReactNode = null;

  if (isLoading) {
    EndIcon = <FaroButtonSpinner />;
  } else if (!isEditMode && isUserSnapshotAdmin(snapshot)) {
    EndIcon = (
      <EditColumnValueIcon
        iconButtonProps={{ className: SNAPSHOT_NAME_EDIT_ICON_CLASS }}
        onClick={enableEditMode}
      />
    );
  }

  function enableEditMode(): void {
    if (isUserSnapshotAdmin(snapshot)) {
      setIsEditMode(true);
    }
  }

  return (
    <Box
      sx={{
        width: isEditMode ? "100%" : "calc(100% - 10px)",
        height: "92%",
        boxShadow: isEditMode
          ? getBottomBorderWithBoxShadow({
              thickness: "2px",
            })
          : "none",
        ...(!isEditMode && {
          "&:hover": {
            [`& .${SNAPSHOT_NAME_EDIT_ICON_CLASS}`]: {
              visibility: "visible",
            },
          },
        }),
      }}
    >
      <Box
        component="div"
        onClick={enableEditMode}
        sx={{
          height: "100%",
          padding: "8px 10px",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          {isEditMode && (
            <FaroSimpleTextField
              variant="standard"
              value={snapshotName}
              className={NO_TRANSLATE_CLASS}
              type={"text"}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  void updateName();
                }
                if (event.key === "Escape") {
                  setSnapshotName(snapshot.name);
                  setIsEditMode(false);
                }

                event.stopPropagation();
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSnapshotName(event.target.value);
              }}
              onBlur={updateName}
              size="small"
              sx={{
                height: "auto",
                display: "inline-flex",
                width: "100%",
                "& .MuiInputBase-root": {
                  fontSize: "12px",
                  fontWeight: "bold",
                },
                "& .MuiInputBase-input": {
                  padding: "0px",
                  color: neutral[800],
                },
              }}
              InputProps={{
                // eslint-disable-next-line @typescript-eslint/naming-convention -- defined in the mui package
                disableUnderline: true,
              }}
            />
          )}

          {!isEditMode && (
            <SphereTooltip
              title={<var>{snapshotName}</var>}
              boxProps={{
                sx: {
                  ...withEllipsis,
                  fontWeight: "bold",
                },
              }}
            >
              <Text variant="heading12" component="var">
                {snapshotName}
              </Text>
            </SphereTooltip>
          )}

          <Box sx={{ marginLeft: "auto" }}>{EndIcon}</Box>
        </Stack>
      </Box>
    </Box>
  );
}
